"use client";

import { useRouter } from "next/navigation";
import { useMemo, useState, type MutableRefObject } from "react";

import { DeskBackButton } from "@/components/DeskBackButton";
import { ExpiryDeskWordmark } from "@/components/ExpiryDeskWordmark";
import { buildDeskNotificationQueue } from "@/lib/deskNotificationQueue";

export type DeskDashboardHandle = {
  updateMed: (updated: Record<string, unknown>, options?: Record<string, unknown>) => Promise<void>;
  setEditingMed: (med: Record<string, unknown> | null) => void;
  setFilterStatus: (v: string) => void;
};

type DeskNotificationsPageProps = {
  meds: Record<string, unknown>[];
  dashboardRef: MutableRefObject<DeskDashboardHandle | null>;
};

/**
 * Full-page action queue (same data as the desk bell). Line actions delegate to the mounted dashboard shell.
 */
export function DeskNotificationsPage({ meds, dashboardRef }: DeskNotificationsPageProps) {
  const router = useRouter();
  const queue = useMemo(() => buildDeskNotificationQueue(meds), [meds]);
  const hasInventory = meds.length > 0;
  const [notifPageMoreExpanded, setNotifPageMoreExpanded] = useState(false);

  const api = () => dashboardRef.current;

  return (
    <div className="ed-pulse-shell">
      <div className="ed-pulse-shell__aurora" aria-hidden />

      <header className="ed-pulse-topbar ed-pulse-topbar--notifications" aria-label="Notifications">
        <div className="ed-pulse-topbar__left">
          <DeskBackButton fallbackHref="/dashboard" />
          <ExpiryDeskWordmark
            variant="nav"
            showTagline={false}
            href="/dashboard"
            className="ed-pulse-topbar__logo"
            linkClassName="ed-desk-brand-link"
          />
        </div>
      </header>

      <main className="ed-pulse-main">
        <section className="ed-pulse-hero ed-pulse-glass">
          <p className="ed-pulse-hero__eyebrow">Desk</p>
          <h1 className="ed-pulse-hero__title">Notifications</h1>
          <p className="ed-pulse-hero__lede">
            Lines that need a date fix, sit inside the 90-day pulse, or are overdue — same queue as the bell, with room to work the list.
          </p>
          <div className="ed-pulse-hero__stats" role="presentation">
            <div className="ed-pulse-stat">
              <span className="ed-pulse-stat__value">{queue.length}</span>
              <span className="ed-pulse-stat__label">in action queue</span>
            </div>
          </div>
        </section>

        <section className="ed-pulse-glass ed-dash-notif-page">
          <div className="ed-dash-top-header__panel-heading" style={{ marginBottom: "0.75rem" }}>
            Action queue
          </div>
          {queue.length > 0 ? (
            <div className="ed-dash-notif-queue">
              {queue.map(({ med, days, needsDate }) => {
                const label = (String(med.name || "Line").trim() || "Line") as string;
                const dayLabel =
                  needsDate || days == null
                    ? "Set expiry date"
                    : days < 0
                      ? `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} overdue`
                      : days === 0
                        ? "Expires today"
                        : `${days} day${days === 1 ? "" : "s"} left`;
                const dayTone = !needsDate && days != null && days < 0 ? "critical" : !needsDate && days != null && days <= 7 ? "soon" : "";
                return (
                  <div key={String(med.id)} className="ed-dash-notif-row">
                    <div className="ed-dash-notif-row__meta">
                      <span className="ed-dash-notif-row__name">{label}</span>
                      <span
                        className={
                          dayTone === "critical"
                            ? "ed-dash-notif-row__days ed-dash-notif-row__days--critical"
                            : dayTone === "soon"
                              ? "ed-dash-notif-row__days ed-dash-notif-row__days--soon"
                              : "ed-dash-notif-row__days"
                        }
                      >
                        {dayLabel}
                      </span>
                    </div>
                    <div className="ed-dash-notif-row__actions">
                      {!needsDate && days != null ? (
                        <>
                          <button
                            type="button"
                            className="ed-dash-notif-row__btn ed-dash-notif-row__btn--moved"
                            onClick={() => void api()?.updateMed({ ...med, status: "item_moved" } as Record<string, unknown>)}
                          >
                            Moved / dispensed
                          </button>
                          <button
                            type="button"
                            className="ed-dash-notif-row__btn ed-dash-notif-row__btn--writeoff"
                            onClick={() => void api()?.updateMed({ ...med, status: "item_expired" } as Record<string, unknown>)}
                          >
                            Write-off
                          </button>
                        </>
                      ) : null}
                      <button
                        type="button"
                        className="ed-dash-notif-row__btn ed-dash-notif-row__btn--edit"
                        onClick={() => {
                          api()?.setEditingMed(med as Record<string, unknown>);
                          void router.push("/dashboard");
                        }}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
          {queue.length === 0 ? (
            <p className="ed-dash-notif-empty" style={{ margin: 0 }}>
              {hasInventory
                ? "Action queue is clear. The badge stays off until a line needs a date fix, sits inside 90 days, or is overdue."
                : "Add lines to your desk to see expiry actions here."}
            </p>
          ) : null}

          <div className="ed-dash-top-header__panel-divider" style={{ margin: "1.25rem 0" }} aria-hidden />
          <button
            type="button"
            className="ed-dash-top-header__panel-item ed-dash-top-header__panel-item--stacked ed-dash-top-header__panel-item--more-disclosure"
            style={{ width: "100%", textAlign: "left" }}
            aria-expanded={notifPageMoreExpanded}
            onClick={() => setNotifPageMoreExpanded((v) => !v)}
          >
            <span className="ed-dash-top-header__panel-item-title">
              More <span aria-hidden>{notifPageMoreExpanded ? "▴" : "▾"}</span>
            </span>
            <span className="ed-dash-top-header__panel-item-sub">
              {notifPageMoreExpanded
                ? "Hide Priority Pulse & dashboard insights"
                : "Show Priority Pulse & dashboard insights (coming soon)"}
            </span>
          </button>
          {notifPageMoreExpanded ? (
            <>
              <div
                className="ed-dash-top-header__panel-item ed-dash-top-header__panel-item--stacked ed-dash-top-header__panel-item--coming-soon"
                aria-disabled="true"
                style={{ marginTop: "0.45rem" }}
              >
                <span className="ed-dash-top-header__panel-item-title">Priority Pulse</span>
                <span className="ed-dash-top-header__panel-item-sub">Coming soon — stay tuned</span>
              </div>
              <div className="ed-dash-top-header__panel-heading" style={{ marginTop: "0.75rem", marginBottom: "0.35rem" }}>
                Dashboard insights
              </div>
              <div
                className="ed-dash-top-header__panel-item ed-dash-top-header__panel-item--stacked ed-dash-top-header__panel-item--coming-soon"
                aria-disabled="true"
              >
                <span className="ed-dash-top-header__panel-item-title">Expiry risk overview</span>
                <span className="ed-dash-top-header__panel-item-sub">Coming soon — stay tuned</span>
              </div>
              <div
                className="ed-dash-top-header__panel-item ed-dash-top-header__panel-item--stacked ed-dash-top-header__panel-item--coming-soon"
                aria-disabled="true"
              >
                <span className="ed-dash-top-header__panel-item-title">Inventory health &amp; intelligence</span>
                <span className="ed-dash-top-header__panel-item-sub">Coming soon — stay tuned</span>
              </div>
              <div
                className="ed-dash-top-header__panel-item ed-dash-top-header__panel-item--stacked ed-dash-top-header__panel-item--coming-soon"
                aria-disabled="true"
              >
                <span className="ed-dash-top-header__panel-item-title">Inventory workspace</span>
                <span className="ed-dash-top-header__panel-item-sub">Coming soon — stay tuned</span>
              </div>
            </>
          ) : null}
        </section>
      </main>
    </div>
  );
}
